import { type ComponentType, type LazyExoticComponent, lazy } from 'react';

const forcedReloadKey = 'tyro:page-refreshed-forced';

function hasAlreadyForcedReload(): boolean {
  return JSON.parse(
    window.localStorage.getItem(forcedReloadKey) || 'false',
  ) as boolean;
}

function clearForcedReload(): void {
  window.localStorage.setItem(forcedReloadKey, 'false');
}

// reload() navigates asynchronously; a promise that never settles suspends the
// caller until it takes effect, rather than running on a half-loaded module graph.
function suspendUntilReload<T>(): Promise<T> {
  return new Promise<T>(() => undefined);
}

// A failed dynamic import means a stale tab is requesting chunk URLs a later
// deploy removed; only a reload pulls the fresh index.html and its chunk map.
// The guard stops a genuinely broken build from reloading forever.
function reloadOnceForStaleChunk<T>(error: unknown): Promise<T> {
  if (hasAlreadyForcedReload()) {
    throw error;
  }

  window.localStorage.setItem(forcedReloadKey, 'true');
  window.location.reload();
  return suspendUntilReload();
}

async function importWithReload<T>(factory: () => Promise<T>): Promise<T> {
  try {
    return await factory();
  } catch (error) {
    return reloadOnceForStaleChunk<T>(error);
  }
}

// Loads dynamic imports (e.g. bootstrap route modules) with stale-chunk reload
// recovery, clearing the guard only once the whole batch resolves. Clearing
// per-import instead would let one import's success re-arm the reload while a
// sibling on a genuinely broken build keeps failing — an infinite loop.
export async function importAllWithReload<T>(
  factories: Array<() => Promise<T>>,
): Promise<T[]> {
  const modules = await Promise.all(
    factories.map((loadModule) => importWithReload(loadModule)),
  );
  clearForcedReload();
  return modules;
}

export const lazyWithRetry = <T extends ComponentType<unknown>>(
  factory: () => Promise<{
    default: T;
  }>,
): LazyExoticComponent<T> =>
  lazy(async () => {
    try {
      const component = await factory();
      clearForcedReload();
      return component;
    } catch (error) {
      return reloadOnceForStaleChunk<{ default: T }>(error);
    }
  });
